// src/components/EnergyComparisonWidget2.js
import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

// Sample data that represents what users might see in the actual analysis
const sampleData = [
  { apartment: '213', '2023-2024': 420, '2024-2025': 385 },
  { apartment: '237', '2023-2024': 510, '2024-2025': 475 },
  { apartment: '251', '2023-2024': 380, '2024-2025': 365 },
  { apartment: '275', '2023-2024': 455, '2024-2025': 440 },
  { apartment: '291', '2023-2024': 395, '2024-2025': 410 },
  // { apartment: '201A', '2023-2024': 95, '2024-2025': 88 },
];

const EnergyComparisonWidget2 = ({ onNavigate }) => {
  const [hoveredApartment, setHoveredApartment] = useState(null);

  // Inline styles for the component (for responsiveness)
  const styles = {
    widget: {
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb',
      position: 'relative',
      overflow: 'hidden'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '20px',
      flexWrap: 'wrap',
      gap: '12px'
    },
    headerContent: {
      flex: 1
    },
    title: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 8px 0'
    },
    description: {
      fontSize: '14px',
      color: '#6b7280',
      margin: 0,
      lineHeight: 1.5
    },
    previewBadge: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      border: '1px solid #fcd34d'
    },
    chartContainer: {
      marginBottom: '20px',
      padding: '10px 0'
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
      gap: '16px',
      marginBottom: '20px'
    },
    statCard: {
      textAlign: 'center',
      padding: '16px 12px',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px solid #e5e7eb'
    },
    statValue: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: '0 0 4px 0'
    },
    statValueImprovement: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#059669',
      margin: '0 0 4px 0'
    },
    statValueIncrease: {
      fontSize: '24px',
      fontWeight: 'bold',
      color: '#dc2626',
      margin: '0 0 4px 0'
    },
    statLabel: {
      fontSize: '12px',
      color: '#6b7280',
      fontWeight: '500',
      margin: 0
    },
    featuresPreview: {
      backgroundColor: '#f8fff4',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #d1fae5',
      marginBottom: '20px'
    },
    featuresTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 12px 0'
    },
    featuresList: {
      listStyle: 'none',
      padding: 0,
      margin: 0
    },
    featureItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '8px',
      fontSize: '14px',
      color: '#374151'
    },
    featureIcon: {
      fontSize: '16px',
      width: '20px',
      textAlign: 'center'
    },
    tooltip: {
      backgroundColor: 'white',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      padding: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
    },
    tooltipTitle: {
      fontWeight: '600',
      marginBottom: '8px',
      color: '#1f2937'
    },
    tooltipItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '4px',
      fontSize: '14px',
      color: '#374151'
    },
    colorIndicator: {
      width: '12px',
      height: '12px',
      borderRadius: '2px'
    },
    tooltipChange: {
      marginTop: '8px',
      fontSize: '14px',
      fontWeight: '500',
      paddingTop: '8px',
      borderTop: '1px solid #e5e7eb'
    },
    tooltipImprovement: {
      color: '#059669'
    },
    tooltipIncrease: {
      color: '#dc2626'
    }
  };

  // Calculate stats from sample data
  const currentYearTotal = sampleData.reduce((sum, item) => sum + item['2024-2025'], 0);
  const previousYearTotal = sampleData.reduce((sum, item) => sum + item['2023-2024'], 0);
  const percentChange = ((currentYearTotal - previousYearTotal) / previousYearTotal * 100).toFixed(1);
  const apartmentsImproved = sampleData.filter(item => item['2024-2025'] < item['2023-2024']).length;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const change = data['2024-2025'] - data['2023-2024'];
      const changePercent = ((change / data['2023-2024']) * 100).toFixed(1);
      
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipTitle}>Appartement {label}</p>
          <p style={styles.tooltipItem}>
            <span style={{...styles.colorIndicator, backgroundColor: '#3B82F6'}}></span>
            Vorig jaar: {data['2023-2024']} eenheden
          </p>
          <p style={styles.tooltipItem}>
            <span style={{...styles.colorIndicator, backgroundColor: '#10B981'}}></span>
            Dit jaar: {data['2024-2025']} eenheden
          </p>
          <p style={{
            ...styles.tooltipChange,
            ...(change < 0 ? styles.tooltipImprovement : styles.tooltipIncrease)
          }}>
            Verandering: {change > 0 ? '+' : ''}{change} eenheden ({changePercent}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.widget}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h3 style={styles.title}>📊 Energieverbruik per maand per appartement</h3>
          <p style={styles.description}>
            Vergelijk energieverbruik tussen verschillende stookseizoenen per appartement
          </p>
        </div>
        {/* <div style={styles.previewBadge}>
          <span>Voorbeeld Data</span>
        </div> */}
      </div>

      <div style={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart 
            data={sampleData} 
            margin={{ top: 10, right: window.innerWidth < 768 ? 10 : 20, left: 10, bottom: 5 }}
            onMouseMove={(state) => {
              if (state && state.activeLabel) {
                setHoveredApartment(state.activeLabel);
              }
            }}
            onMouseLeave={() => setHoveredApartment(null)}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis 
              dataKey="apartment" 
              tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis 
              tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
              axisLine={{ stroke: '#d1d5db' }}
              label={{ value: 'Eenheden', angle: -90, position: 'insideLeft' }}
              width={window.innerWidth < 768 ? 30 : 50}
            />
            <Tooltip content={<CustomTooltip />} />
            {window.innerWidth >= 768 && (
              <Legend 
                wrapperStyle={{ paddingTop: '10px' }}
                iconType="rect"
              />
            )}
            <Bar 
              dataKey="2023-2024" 
              name="Stookseizoen 2023-2024"
              fill="#3B82F6" 
              radius={[2, 2, 0, 0]}
              opacity={hoveredApartment ? 0.7 : 1}
            />
            <Bar 
              dataKey="2024-2025" 
              name="Stookseizoen 2024-2025"
              fill="#10B981" 
              radius={[2, 2, 0, 0]}
              opacity={hoveredApartment ? 0.7 : 1}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile Legend */}
      {window.innerWidth < 768 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '16px',
          marginBottom: '20px',
          fontSize: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#3B82F6', borderRadius: '2px' }}></div>
            <span>2023-2024</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{ width: '12px', height: '12px', backgroundColor: '#10B981', borderRadius: '2px' }}></div>
            <span>2024-2025</span>
          </div>
        </div>
      )}

      {/* <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{apartmentsImproved}/6</div>
          <div style={styles.statLabel}>Appartementen verbeterd</div>
        </div>
        <div style={styles.statCard}>
          <div style={parseFloat(percentChange) < 0 ? styles.statValueImprovement : styles.statValueIncrease}>
            {percentChange}%
          </div>
          <div style={styles.statLabel}>Totale verandering</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{currentYearTotal.toLocaleString()}</div>
          <div style={styles.statLabel}>Huidig totaal verbruik</div>
        </div>
      </div> */}

      {/* <div style={styles.featuresPreview}>
        <h4 style={styles.featuresTitle}>🔍 In de gedetailleerde analyse vind je:</h4>
        <ul style={styles.featuresList}>
          <li style={styles.featureItem}>
            <span style={styles.featureIcon}>📈</span>
            Maandelijkse vergelijking tussen stookseizoenen
          </li>
          <li style={styles.featureItem}>
            <span style={styles.featureIcon}>🔥</span>
            Verbruik per meter (radiator) binnen elk appartement
          </li>
          <li style={styles.featureItem}>
            <span style={styles.featureIcon}>📊</span>
            Cumulatieve verbruikstrends door het jaar heen
          </li>
          <li style={styles.featureItem}>
            <span style={styles.featureIcon}>🎯</span>
            Voorspellingen gebaseerd op actuele data
          </li>
        </ul>
      </div> */}

      <button 
        onClick={() => onNavigate && onNavigate('/energy-analysis')}
        style={{
          width: '100%',
          padding: '12px 24px',
          backgroundColor: '#246834',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
          marginTop: '20px'
        }}
        onMouseOver={(e) => {
          e.target.style.backgroundColor = '#1e5728';
          e.target.style.transform = 'translateY(-2px)';
        }}
        onMouseOut={(e) => {
          e.target.style.backgroundColor = '#246834';
          e.target.style.transform = 'translateY(0)';
        }}
      >
        <span>Bekijk Gedetailleerde Analyse</span>
        <span style={{ fontSize: '18px' }}>→</span>
      </button>
    </div>
  );
};

export default EnergyComparisonWidget2;